import type { SessionRow } from '../types/sessions';

function normalizeWhitespace(value: string): string {
  return value.replace(/\s+/g, ' ').trim();
}

function looksLikeUuid(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
}

function titleCaseWords(value: string): string {
  return value
    .split(' ')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function agentIdDisplayName(agentId: string): string {
  const normalized = normalizeWhitespace(agentId.replace(/[-_]+/g, ' '));
  if (!normalized) return 'Session';
  if (normalized.toLowerCase() === 'main') return 'Main';
  return titleCaseWords(normalized);
}

function looksLikeMetadata(value: string): boolean {
  if (!value) return true;
  if (value.includes('\n')) return true;
  if (/^agent:[^:]+:/.test(value)) return true;
  if (/^session[:=]/i.test(value)) return true;
  if (/^[[{].*[}\]]$/.test(value)) return true;
  if (/^[a-z0-9_-]+=[^\s]+/i.test(value)) return true;
  if (looksLikeUuid(value)) return true;
  return false;
}

function isMainAgentSession(sessionKey: string): boolean {
  const parts = sessionKey.split(':');
  return parts.length === 3 && parts[0] === 'agent' && parts[2] === 'main';
}

function cleanCandidate(value: string | null | undefined): string | null {
  if (!value) return null;

  const cleaned = normalizeWhitespace(value)
    .replace(/^["'`]+|["'`]+$/g, '')
    .replace(/\s+session$/i, '');

  if (!cleaned) return null;
  if (looksLikeMetadata(cleaned)) return null;

  return cleaned;
}

function fallbackFromSessionKey(sessionKey: string): string {
  const parts = sessionKey.split(':');
  if (parts.length >= 3 && parts[0] === 'agent') {
    const agentId = parts[1] ?? '';
    const tail = parts.slice(2).join(':');

    if (tail === 'main') {
      return agentIdDisplayName(agentId);
    }

    if (tail.startsWith('subagent:')) {
      return 'Sub-agent';
    }

    return agentIdDisplayName(agentId);
  }

  const lastSegment = sessionKey.split(':').pop() ?? sessionKey;
  if (looksLikeUuid(lastSegment)) return 'Session';

  const gatewayAgentMatch = lastSegment.match(/^(?:g-)?agent-([a-z0-9_-]+)-main$/i);
  if (gatewayAgentMatch?.[1]) {
    return agentIdDisplayName(gatewayAgentMatch[1]);
  }

  const normalized = normalizeWhitespace(lastSegment.replace(/[-_]+/g, ' '));
  return normalized ? titleCaseWords(normalized) : 'Session';
}

function isQuickSwitchFriendlyLabel(value: string): boolean {
  return value.length <= 24
    && !/[`{}[\]]/.test(value)
    && !/:/.test(value)
    && !/untrusted metadata/i.test(value);
}

/**
 * Build a compact human label for a session.
 *
 * Priority:
 * 1. User/custom label
 * 2. Session display name / derived title
 * 3. Agent display name supplied by the caller
 * 4. Sanitized fallback from the session key
 */
export function sessionDisplayName(session: SessionRow, agentDisplayName?: string): string {
  const customLabel = cleanCandidate(session.label);
  if (customLabel) {
    return customLabel;
  }

  const cleanAgentName = cleanCandidate(agentDisplayName);
  if (isMainAgentSession(session.key) && cleanAgentName) {
    return cleanAgentName;
  }

  const candidates = [
    session.displayName,
    session.derivedTitle,
    cleanAgentName,
  ];

  for (const candidate of candidates) {
    const cleaned = cleanCandidate(candidate);
    if (cleaned) return cleaned;
  }

  return fallbackFromSessionKey(session.key);
}

export function sessionVoiceIdentityName(sessionKey: string, agentDisplayName?: string): string {
  const cleanAgentName = cleanCandidate(agentDisplayName);
  if (cleanAgentName) {
    return cleanAgentName;
  }

  return fallbackFromSessionKey(sessionKey);
}

export function sessionQuickSwitchLabel(session: SessionRow, agentDisplayName?: string): string {
  const customLabel = cleanCandidate(session.label);
  if (customLabel && isQuickSwitchFriendlyLabel(customLabel)) {
    return customLabel;
  }

  const cleanAgentName = cleanCandidate(agentDisplayName);
  if (cleanAgentName && isQuickSwitchFriendlyLabel(cleanAgentName)) {
    return cleanAgentName;
  }

  const keyFallback = fallbackFromSessionKey(session.key);
  if (isQuickSwitchFriendlyLabel(keyFallback)) {
    return keyFallback;
  }

  const displayName = cleanCandidate(session.displayName);
  if (displayName && isQuickSwitchFriendlyLabel(displayName)) {
    return displayName;
  }

  const derivedTitle = cleanCandidate(session.derivedTitle);
  if (derivedTitle && isQuickSwitchFriendlyLabel(derivedTitle)) {
    return derivedTitle;
  }

  return keyFallback;
}

export function sessionDisplayMonogram(label: string): string {
  const words = normalizeWhitespace(label)
    .replace(/\b(session|agent)\b/gi, '')
    .split(' ')
    .filter(Boolean);

  if (words.length === 0) return '?';
  if (words.length === 1) return words[0].charAt(0).toUpperCase();
  return `${words[0].charAt(0)}${words[1].charAt(0)}`.toUpperCase();
}
